import { SITE_CONFIG, absoluteUrl, getSiteUrl } from './siteConfig';

/**
 * @param {string[]} keys
 * @returns {object[]}
 */
export function buildPageJsonLd(keys = []) {
    const blocks = [];
    for (const key of keys) {
        const builder = JSON_LD_BUILDERS[key];
        if (builder) {
            blocks.push(builder());
        }
    }
    return blocks;
}

/** @type {Record<string, () => object>} */
const JSON_LD_BUILDERS = {
    organization() {
        return {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            '@id': `${getSiteUrl()}/#organization`,
            name: SITE_CONFIG.siteName,
            url: getSiteUrl(),
            logo: {
                '@type': 'ImageObject',
                url: absoluteUrl('/logo512.png'),
                width: 512,
                height: 512,
            },
            image: absoluteUrl('/og-cover.png'),
            foundingDate: '2019',
            contactPoint: {
                '@type': 'ContactPoint',
                telephone: SITE_CONFIG.contact.phone,
                email: SITE_CONFIG.contact.email,
                contactType: 'customer service',
                areaServed: 'KZ',
                availableLanguage: ['Russian', 'Kazakh'],
            },
            sameAs: [SITE_CONFIG.social.instagram, SITE_CONFIG.social.whatsapp].filter(Boolean),
        };
    },

    webSite() {
        return {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: SITE_CONFIG.productName,
            url: getSiteUrl(),
            inLanguage: 'ru',
            publisher: {
                '@type': 'Organization',
                '@id': `${getSiteUrl()}/#organization`,
            },
        };
    },

    webApplication() {
        return {
            '@context': 'https://schema.org',
            '@type': 'WebApplication',
            name: SITE_CONFIG.productName,
            url: getSiteUrl(),
            applicationCategory: 'BusinessApplication',
            applicationSubCategory: 'DesignApplication',
            operatingSystem: 'Web',
            browserRequirements: 'Requires JavaScript. Requires a modern web browser.',
            offers: {
                '@type': 'Offer',
                price: '0',
                priceCurrency: 'KZT',
                availability: 'https://schema.org/InStock',
            },
            description:
                'Онлайн-сервис 3D моделирования и подбора оборудования для бассейна: насосы, фильтры, теплообменники, прожекторы, лестница, закладные элементы. PDF-спецификация бесплатно.',
            featureList: '3D визуализация бассейна в реальном времени, Автоматический расчёт насоса и фильтра по объёму чаши, Подбор теплообменника и дезинфекции, PDF-спецификация с артикулами и ценами, Pahlen, Emaux, Landy, Reviglass',
            screenshot: absoluteUrl('/og-cover.png'),
            inLanguage: 'ru',
            areaServed: {
                '@type': 'Country',
                name: 'Kazakhstan',
            },
            publisher: {
                '@type': 'Organization',
                '@id': `${getSiteUrl()}/#organization`,
            },
        };
    },

    localBusiness() {
        return {
            '@context': 'https://schema.org',
            '@type': 'LocalBusiness',
            '@id': `${getSiteUrl()}/#localbusiness`,
            name: SITE_CONFIG.siteName,
            url: getSiteUrl(),
            logo: absoluteUrl('/logo512.png'),
            image: [absoluteUrl('/og-cover.png'), absoluteUrl('/logo512.png')],
            description:
                'Подбор и поставка профессионального оборудования для бассейнов в Казахстане. Насосы Pahlen, фильтры Emaux, теплообменники, прожекторы, лестницы. Работаем с 2019 года.',
            foundingDate: '2019',
            telephone: SITE_CONFIG.contact.phone,
            email: SITE_CONFIG.contact.email,
            address: {
                '@type': 'PostalAddress',
                addressLocality: 'Алматы',
                addressRegion: 'Алматы',
                addressCountry: 'KZ',
            },
            geo: {
                '@type': 'GeoCoordinates',
                latitude: 43.238293,
                longitude: 76.945465,
            },
            areaServed: [
                { '@type': 'City', name: 'Алматы' },
                { '@type': 'City', name: 'Астана' },
                { '@type': 'City', name: 'Шымкент' },
                { '@type': 'Country', name: 'Kazakhstan' },
            ],
            priceRange: '₸₸',
            currenciesAccepted: 'KZT',
            paymentAccepted: 'Cash, Credit Card, Bank Transfer',
            openingHoursSpecification: {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
                opens: '09:00',
                closes: '18:00',
            },
            sameAs: [SITE_CONFIG.social.instagram, SITE_CONFIG.social.whatsapp].filter(Boolean),
            parentOrganization: {
                '@type': 'Organization',
                '@id': `${getSiteUrl()}/#organization`,
            },
        };
    },

    howTo() {
        return {
            '@context': 'https://schema.org',
            '@type': 'HowTo',
            name: 'Как подобрать оборудование для бассейна онлайн',
            description:
                'Пошаговое руководство по подбору насоса, фильтра, теплообменника и отделки для частного бассейна с помощью сервиса Welland Create. Занимает 3–5 минут, бесплатно.',
            totalTime: 'PT5M',
            tool: [
                { '@type': 'HowToTool', name: 'Welland Create — онлайн-сервис подбора оборудования для бассейна' },
            ],
            step: [
                {
                    '@type': 'HowToStep',
                    position: 1,
                    name: 'Укажите размеры чаши бассейна',
                    text: 'Введите длину, ширину и глубину бассейна с помощью ползунков. Сервис поддерживает размеры от 3×2 до 25×10 м. 3D модель обновится мгновенно.',
                    url: `${getSiteUrl()}#top`,
                },
                {
                    '@type': 'HowToStep',
                    position: 2,
                    name: 'Выберите тип отделки',
                    text: 'Укажите материал отделки чаши: армированная ПВХ плёнка (CGT, Haogenplast, Alkorplan) или стеклянная мозаика (Reviglass, Altoglass, Elbe).',
                    url: `${getSiteUrl()}#top`,
                },
                {
                    '@type': 'HowToStep',
                    position: 3,
                    name: 'Отметьте необходимое оборудование',
                    text: 'Выберите комплект: фильтрация и насос (Pahlen, Emaux), подогрев воды (теплообменник/электронагреватель), дезинфекция (хлоринатор/UV), RGB прожекторы, лестница из нержавеющей стали, закладные элементы — скиммеры и форсунки.',
                    url: `${getSiteUrl()}#top`,
                },
                {
                    '@type': 'HowToStep',
                    position: 4,
                    name: 'Получите автоматический расчёт',
                    text: 'Сервис рассчитает мощность насоса, производительность фильтра и теплообменника по инженерным формулам, исходя из объёма чаши и типа бассейна. Расчёт обновляется в реальном времени.',
                    url: `${getSiteUrl()}#top`,
                },
                {
                    '@type': 'HowToStep',
                    position: 5,
                    name: 'Скачайте PDF-спецификацию',
                    text: 'Зарегистрируйтесь (бесплатно) и скачайте готовую PDF-спецификацию с артикулами, техническими характеристиками и актуальными ценами. Передайте подрядчику или отправьте нам для выставления счёта.',
                    url: `${getSiteUrl()}/dashboard`,
                },
            ],
        };
    },

    faqPage() {
        return {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'Это бесплатно?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Да, полностью и без каких-либо условий. Конфигуратор, подбор оборудования, расчёт стоимости и выгрузка PDF-спецификации — всё это бесплатно. Никаких скрытых платежей и звонков «для получения цены» — всё прозрачно сразу на экране.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Вы строите бассейны?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Нет, строительством мы не занимаемся. Наша специализация — подбор и поставка профессионального оборудования для бассейнов, саун и хаммамов. Монтаж выполняет ваш подрядчик, а мы при необходимости дадим технические рекомендации и схемы подключения.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Насколько точен расчёт?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Подбор оборудования выполняется автоматически по инженерным формулам — система сама рассчитывает мощность насоса, объём фильтра, производительность теплообменника. Небольшое расхождение в пределах 5–10% возможно из-за колебаний курса. Финальная стоимость фиксируется в договоре.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Какие бренды есть?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Работаем с проверенными производителями из Европы и Азии: Pahlen, Emaux, Landy, Aqua-Viva, Elbe, Reviglass, Glong, Max Dapra, HX, Altoglass, CGT и другими. Под любой бюджет подберём оптимальное сочетание надёжности и цены.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Что входит в PDF-спецификацию?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Полный перечень оборудования с артикулами, техническими характеристиками и ценами на момент расчёта. Документ удобно передать подрядчику или сразу прислать нам для выставления счёта.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Сколько стоит оборудование для бассейна в Казахстане?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Стоимость комплекта зависит от размера чаши и выбранных брендов. Ориентировочно: скиммерный бассейн 6×3 м — от 1 500 000 ₸, 8×4 м — от 2 500 000 ₸, переливной 10×5 м — от 4 000 000 ₸. Точный расчёт со всеми артикулами и ценами сформирует конфигуратор бесплатно за 3 минуты.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Как получить консультацию?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Настройте параметры вашего объекта в конфигураторе и нажмите «Получить консультацию» — менеджер свяжется в течение часа. Можно также написать напрямую в WhatsApp: +7 777 229-62-25 или позвонить.',
                    },
                },
            ],
        };
    },
};
